import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MyCollectionLayout from "./MyCollectionLayout";
import McBackLink from "./McBackLink";
import { listCollectorItems } from "../../services/collectorApi";
import { getCollectorToken } from "../../helpers/collectorSession";
import { collectorRecordKey } from "../../helpers/collectorIds";
import { firstDemoItem } from "../../helpers/collectorRooms";

const FAQS = [
  {
    q: "What is the difference between Scanned and My pieces?",
    a: "Scanned is a receipt of HastKalaPramanak tags you tapped — date, tag name, what the certificate still allows. My pieces is the cloth you keep: photo, title, story, and private notes. A scan is not a claim that you own the piece.",
  },
  {
    q: "Where do scanned tags go?",
    a: "Hold your phone to the physical NFC tag, or scan the printed QR. Open the certificate and verify your phone. That tap is saved to Scanned automatically.",
  },
  {
    q: "Why didn’t my scan appear in My pieces?",
    a: "Scans stay in Scanned. There is no “I bought this” button yet. To journal a piece you own, open My pieces and choose Add a piece.",
  },
  {
    q: "What is My pieces?",
    a: "Pieces you keep — bought, inherited, commissioned, or journaled. Add a photo and a title; a short story helps. Price and where you acquired it stay private.",
  },
  {
    q: "How do I add a piece I already own?",
    a: "Open My pieces and tap Add a piece. One cover photo and a title are enough. That journal never comes from an NFC tap.",
  },
  {
    q: "What happens after about 14 days on a scan?",
    a: "Certificate details hide unless the workshop re-shares them. The physical tag still verifies authenticity. Use Ask studio for full certificate on that scan — that request lives only in Scanned.",
  },
  {
    q: "How do QR codes work?",
    a: "Open a piece in My pieces, then Share & print. Anyone who scans that QR opens only that piece. Your collection list, price, and purchase place never appear.",
  },
  {
    q: "What does a visitor see on my public page?",
    a: "Cover photo, title, maker or studio if you shared it, a certificate badge only if the piece is HKP-certified and owned, and your story — or “The owner hasn’t added a note yet.” Never a blank page, never price or purchase place.",
  },
  {
    q: "Can I print a card from a scan?",
    a: "No. Print, PDF, and 4×6 cards are only for pieces you keep in My pieces. A tap you did not buy should not become stationery.",
  },
  {
    q: "What is Minimal vs Luxury on Download card?",
    a: "Luxury is a 4×6 card with the photograph and a short story. Minimal is title and QR only. Luxury is the default. Print / PDF uses the same choice.",
  },
  {
    q: "Is there an in-app camera to scan tags?",
    a: "Not in this version. Use the physical tag or the QR printed with the piece, then verify your phone on the certificate page.",
  },
  {
    q: "What is Try a demo piece?",
    a: "A sample journal so you can see how a piece looks. Samples are not listed in Scanned or My pieces.",
  },
  {
    q: "Why is there no certificate on some My pieces?",
    a: "A certificate shows on My pieces only when the piece is HKP-certified and marked owned. Most journals you add yourself are external — photo and story, no HKP cert, which is expected.",
  },
  {
    q: "What happens when my trial ends?",
    a: "You stay on the free plan. Scans and your own pieces stay unlimited, and you can always edit them. Nothing is deleted. Plan details live in Settings.",
  },
  {
    q: "How does the passkey work?",
    a: "Set a passkey in Settings (trial or premium), then mark a piece private. Visitors need that passkey to read the journal.",
  },
  {
    q: "How do I archive a piece?",
    a: "Open the piece, choose Archive. Restore it later from Settings. Public QR shows it is no longer available while archived.",
  },
];

const MyCollectionHelp: React.FC = () => {
  const [open, setOpen] = useState(0);
  const [demoPath, setDemoPath] = useState("");

  useEffect(() => {
    if (!getCollectorToken()) return;
    listCollectorItems()
      .then((res) => {
        const items = (res.data?.data?.items || []) as Array<{
          itemType?: string;
          status?: string;
          isSampleItem?: boolean;
          id?: string;
          _id?: string;
          collectorItemId?: string;
        }>;
        const demo = firstDemoItem(items);
        const key = demo ? collectorRecordKey(demo) : "";
        if (key) setDemoPath(`/my-collection/item/${key}`);
      })
      .catch(() => undefined);
  }, []);

  return (
    <MyCollectionLayout>
      <div className="mc-help-page">
        <McBackLink to="/my-collection">Back to My pieces</McBackLink>
        <h1 className="h3 mb-3">Help</h1>
        <section className="mc-card mb-3">
          <h2 className="h6 text-uppercase text-muted mb-2">Support</h2>
          <p className="small text-muted mb-2">Questions about a tag, a journal, or your plan — write or message us.</p>
          <ul className="mc-support-links list-unstyled mb-0">
            <li>
              <a href="https://wa.link/5edo3n" target="_blank" rel="noreferrer">
                WhatsApp
              </a>
            </li>
            <li>
              <a href="mailto:kunal@hastkalapramanak.in">kunal@hastkalapramanak.in</a>
            </li>
          </ul>
        </section>
        <p className="mb-3">
          <Link to="/my-collection/how-to-scan">How to scan</Link>
          {demoPath && (
            <>
              {" · "}
              <Link to={demoPath}>Try a demo piece</Link>
            </>
          )}
        </p>
        <h2 className="h6 text-uppercase text-muted mb-2">FAQs</h2>
        <div className="mc-faq">
          {FAQS.map((item, index) => (
            <details
              key={item.q}
              open={open === index}
              onToggle={(e) => {
                const el = e.currentTarget;
                if (el.open) setOpen(index);
                else if (open === index) setOpen(-1);
              }}
            >
              <summary>{item.q}</summary>
              <p className="mb-0 mt-2 small text-muted">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </MyCollectionLayout>
  );
};

export default MyCollectionHelp;
